import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { bidService } from '../../services/bidService';
import type { Bid as BidType, CreateBidDTO } from '../../types/bid';
import i18n from '../../i18n';
import { updateTaskAfterBidAccepted } from './tasksSlice';

interface BidsState {
  items: BidType[];
  loading: boolean;
  error: string | null;
}

const initialState: BidsState = {
  items: [],
  loading: false,
  error: null
};

export const fetchMyBids = createAsyncThunk(
  'bids/fetchMyBids',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching bids from service...');
      const response = await bidService.getMyBids();
      console.log('Bids fetched in thunk:', response);
      return response;
    } catch (error: any) {
      console.error('Error fetching bids:', error);
      return rejectWithValue(error.response?.data?.message || i18n.t('bids.errors.fetch_failed'));
    }
  }
);

export const createBid = createAsyncThunk(
  'bids/createBid',
  async (bidData: CreateBidDTO, { rejectWithValue, dispatch }) => {
    try {
      const response = await bidService.createBid(bidData);
      // След успешно създаване, обновяваме списъка с оферти
      dispatch(fetchMyBids());
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || i18n.t('bids.errors.create_failed'));
    }
  }
);

export const acceptBid = createAsyncThunk(
  'bids/acceptBid',
  async (bidId: number, { rejectWithValue, dispatch }) => {
    try {
      const response = await bidService.acceptBid(bidId);
      // След успешно приемане, обновяваме списъка с оферти
      dispatch(fetchMyBids());
      // Обновяваме и задачата
      dispatch(updateTaskAfterBidAccepted(response.task_id));
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || i18n.t('bids.errors.accept_failed'));
    }
  }
);

export const deleteBid = createAsyncThunk(
  'bids/deleteBid',
  async (bidId: number, { rejectWithValue }) => {
    try {
      await bidService.deleteBid(bidId);
      return bidId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || i18n.t('bids.errors.delete_failed'));
    }
  }
);

const bidsSlice = createSlice({
  name: 'bids',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch bids
      .addCase(fetchMyBids.pending, (state) => {
        console.log('fetchMyBids.pending - Setting loading state');
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyBids.fulfilled, (state, action) => {
        console.log('fetchMyBids.fulfilled - Received data:', action.payload);
        state.items = action.payload;
        state.loading = false;
        console.log('State after update:', state.items);
      })
      .addCase(fetchMyBids.rejected, (state, action) => {
        console.error('fetchMyBids.rejected - Error:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create bid
      .addCase(createBid.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBid.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = [...state.items, action.payload];
      })
      .addCase(createBid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Accept bid
      .addCase(acceptBid.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptBid.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.map(bid => 
          bid.id === action.payload.id ? action.payload : bid
        );
      })
      .addCase(acceptBid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete bid
      .addCase(deleteBid.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteBid.fulfilled, (state, action) => {
        state.items = state.items.filter(bid => bid.id !== action.payload);
      })
      .addCase(deleteBid.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const selectBids = (state: RootState) => state.bids.items;
export const selectBidsLoading = (state: RootState) => state.bids.loading;
export const selectBidsError = (state: RootState) => state.bids.error;

export default bidsSlice.reducer; 